import React, { useState } from 'react';
import { HugeiconsIcon } from "@hugeicons/react";
import { FileEditIcon, UserIcon, Shield01Icon, AlertIcon, MailIcon, AiPhoneIcon } from "@hugeicons/core-free-icons";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { SortOption } from "../components/search/searchUtils";

export default function TermsOfUse() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('name-asc');
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[var(--background)]">
      <Header 
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        sortBy={sortBy}
        setSortBy={setSortBy}
        filterMenuOpen={filterMenuOpen}
        setFilterMenuOpen={setFilterMenuOpen}
        sortMenuOpen={sortMenuOpen}
        setSortMenuOpen={setSortMenuOpen}
      />
      
      {/* Hero Section */}
      <section className="w-full bg-white">
        <div className="container py-0">
          <div className="text-center pt-16 pb-10 md:pt-[120px] md:pb-24">
            <div className="mb-4">
              <h1 className="text-4xl text-[var(--foreground)]">Terms of Use</h1>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="w-full py-10 md:py-24 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <div className="space-y-8">
            
            {/* Last Updated */}
            <div className="mb-8">
              <p className="bodysmall text-[var(--dispensary-gray-500)]">
                Last updated: {new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>

            {/* Acceptance of Terms */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Acceptance of Terms
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  By accessing and using our pharmacy services, you accept and agree to be bound by the terms 
                  and provision of this agreement. If you do not agree to abide by the above, please do not 
                  use this service.
                </p>
              </div>
            </div>

            {/* Use License */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Use License
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  Permission is granted to temporarily download one copy of the materials (information or software) 
                  on our website for personal, non-commercial transitory viewing only. This is the grant of a license, 
                  not a transfer of title, and under this license you may not:
                </p>
                <ul className="list-disc list-inside space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)] ml-4">
                  <li>Modify or copy the materials</li>
                  <li>Use the materials for any commercial purpose or for any public display</li>
                  <li>Attempt to reverse engineer any software contained on our website</li>
                  <li>Remove any copyright or other proprietary notations from the materials</li>
                  <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
                </ul>
              </div>
            </div>

            {/* Prescription Requirements */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Prescription Requirements
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  For prescription medications, you must:
                </p>
                <ul className="list-disc list-inside space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)] ml-4">
                  <li>Provide a valid prescription from a licensed healthcare provider</li>
                  <li>Ensure the prescription is current and not expired</li>
                  <li>Provide accurate medical information and allergy history</li>
                  <li>Confirm your identity and contact information</li>
                  <li>Agree to consult with our pharmacists when required</li>
                </ul>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Disclaimer
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  The materials on our website are provided on an 'as is' basis. We make no warranties, 
                  expressed or implied, and hereby disclaim and negate all other warranties including without 
                  limitation, implied warranties or conditions of merchantability, fitness for a particular 
                  purpose, or non-infringement of intellectual property or other violation of rights.
                </p>
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  Further, we do not warrant or make any representations concerning the accuracy, likely results, 
                  or reliability of the use of the materials on our website or otherwise relating to such 
                  materials or on any sites linked to this site.
                </p>
              </div>
            </div>

            {/* Limitations */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Limitations
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  In no event shall our pharmacy or its suppliers be liable for any damages (including, 
                  without limitation, damages for loss of data or profit, or due to business interruption) 
                  arising out of the use or inability to use the materials on our website, even if we or 
                  our authorized representative has been notified orally or in writing of the possibility 
                  of such damage.
                </p>
              </div>
            </div>

            {/* Accuracy of Materials */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Accuracy of Materials
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  The materials appearing on our website could include technical, typographical, or 
                  photographic errors. We do not warrant that any of the materials on our website are 
                  accurate, complete, or current. We may make changes to the materials contained on 
                  our website at any time without notice.
                </p>
              </div>
            </div>

            {/* Links */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Links
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  We have not reviewed all of the sites linked to our website and are not responsible 
                  for the contents of any such linked site. The inclusion of any link does not imply 
                  endorsement by us of the site. Use of any such linked website is at the user's own risk.
                </p>
              </div>
            </div>

            {/* Modifications */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Modifications
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  We may revise these terms of service for our website at any time without notice. 
                  By using this website, you are agreeing to be bound by the then current version 
                  of these Terms of Service.
                </p>
              </div>
            </div>

            {/* Governing Law */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Governing Law
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  These terms and conditions are governed by and construed in accordance with the laws 
                  of Nigeria and you irrevocably submit to the exclusive jurisdiction of the courts 
                  in that location.
                </p>
              </div>
            </div>

            {/* Contact Us */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Contact Us
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  If you have any questions about these Terms of Use, please contact us:
                </p>
                <div className="space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  <div className="flex items-center gap-2">
                    <HugeiconsIcon icon={MailIcon} size={16} />
                    <span>Email: legal@pharmacy.com</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <HugeiconsIcon icon={AiPhoneIcon} size={16} />
                    <span>Phone: +234 901 234 5678</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
} 